//************************************************************************ /
// FUNDRAISER CARD
// Used for fundraisers. Includes financial goals and progress bar.
// User can "like" and "view stories" related to it.
//************************************************************************ /

import React, { useState } from "react";
import {
  Card,
  Divider,
  DecoratedImage,
} from "@dreambigger/design-system/src/components";
import {
  CardActions,
  CardDetails,
  CardFundraisingGoal,
} from "@dreambigger/shared/src/components/board/feed-cards/card-components";
import {
  EventCommunityFeedItem,
  Event,
  Community,
  User,
} from "@dreambigger/shared/src/types";
import anim from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import { useRouter } from "next/router";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { useGA, useMixpanel, useSegment } from "@dreambigger/shared/src/hooks";
import {
  optimizeCloudinaryImg,
  generateCloudinaryUrl,
} from "@dreambigger/shared/src/utils";

type FundraiserCardProps = {
  feedItem: EventCommunityFeedItem | Event;
  community: Community;
  currentUser?: User;
};

const FundraiserCard = ({
  feedItem,
  community,
  currentUser,
}: FundraiserCardProps) => {
  const {
    id,
    storyCount,
    images,
    name,
    host,
    startAt,
    endAt,
    campaign,
    likes,
    iLiked,
    location,
    allDay,
    pinned,
    timeZone,
  } = feedItem;

  // Return if no timezone is entered - All times will be wrong
  if (!timeZone) {
    return <></>;
  }

  // Refers to banner that displays on <DecoratedImage> - "HAPPENING NOW!". Enabled/disabled in <CardDetails>
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const router = useRouter();

  // Load Analytics
  const googleAnalytics = useGA();
  const mixpanel = useMixpanel();
  const segment = useSegment();

  const fundraiserUrl = `/events/${id}/detail`;

  const handleFundraiserClick = () => {
    window.location.pathname = fundraiserUrl;
  };

  const getFundraiserImage = (images?: any) => {
    if (!images?.[0]) {
      return "";
    }
    if (images[0].meta?.src) {
      return optimizeCloudinaryImg(images[0].meta.src);
    }
    if (!images[0].imageVersion) {
      return "";
    }
    return generateCloudinaryUrl(images[0].imageVersion, images[0].imageId);
  };

  const handleCardClick = () => {
    // Handle the analytics event where a user clicks the card.
    googleAnalytics.event({
      category: "Card Selection",
      action: "CTA Text:View Event",
      label: "Card Type:Fundraiser",
      value: 1,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.CARD_SELECTED,
      properties: {
        "Card Type": "Fundraiser",
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
      },
      mixpanel,
      segment,
    });
  };

  return (
    <Card
      onClick={handleCardClick}
      bodyStyle={{ padding: 0 }}
      cover={
        images && (
          <DecoratedImage
            alt="Fundraiser Img"
            src={getFundraiserImage(images)}
            titleClass="white fwb f-5"
            bannerText="Happening Now!"
            bannerEnabled={bannerEnabled}
            onClick={handleFundraiserClick}
            pinned={pinned}
            loading="lazy"
          />
        )
      }
      className={`${anim.enter_m} lift mh-auto bg-white pointer`}
    >
      {/* CONTENT ------------------------------------------------ */}
      {/* Campaign Details */}
      <div onClick={handleFundraiserClick} className="ph-5 pv-4">
        <CardDetails
          allDay={allDay}
          title={name || ""}
          host={host || ""}
          location={location.name || ""}
          startAt={startAt || ""}
          endAt={endAt || ""}
          font={"proxima-nova"}
          setBannerEnabled={setBannerEnabled}
          timeZone={timeZone}
        />
      </div>
      {/* Goal and Progress Bar - See component for conditions in which it is displayed*/}
      {campaign && (
        <CardFundraisingGoal
          community={community}
          campaign={campaign}
          user={currentUser}
        />
      )}

      <Divider />
      {/* CARD ACTIONS BAR -------------------------------------- */}
      <CardActions
        id={id}
        user={currentUser}
        cardType="fundraiser"
        userHasLiked={iLiked || false}
        likeEnabled={true}
        storiesEnabled={true}
        likes={likes || 0}
        numOfStories={storyCount}
        ctaTitle={"DONATE NOW"}
        ctaLink={fundraiserUrl}
      />
    </Card>
  );
};

export default FundraiserCard;
